<template>
  <main
    class="metrics"
    :class="{ loading }"
  >
    <Header>
      <router-link
        :to="{ name: 'settings' }"
        class="header__action"
      >
        <IconSettings />
        Configurações
      </router-link>
      <router-link
        :to="{ name: 'pagelist' }"
        class="header__action"
      >
        Páginas
      </router-link>
      <router-link
        :to="{ name: 'modallist' }"
        class="header__action"
      >
        Pop-ups
      </router-link>
      <router-link
        :to="{ name: 'metrics' }"
        class="header__action header__action--disabled"
      >
        Métricas
      </router-link>
      <h2 class="hotel-title">
        {{ siteHostname }}
      </h2>
    </Header>

    <div class="metrics__content custom-scrollbar">
      <div class="metrics__container">
        <div class="metrics__dashboard">
          <div class="card__wrapper">
            <div class="card">
              <div class="left-side">
                <header class="card__title">Leads coletados pelo site</header>
                <div class="card__content">
                  <Loader
                    v-if="loading"
                    color="#3e9aa6"
                  ></Loader>
                  <LineChart
                    v-else
                    :years-data="metrics.leads"
                  ></LineChart>
                </div>
              </div>
              <div class="right-side">
                <header class="card__title">
                  Top 5 páginas que mais capturam leads
                </header>
                <div class="card__content">
                  <Loader
                    v-if="loading"
                    color="#3e9aa6"
                  ></Loader>
                  <CustomTable
                    v-else
                    id="page-list"
                    ref="customTableLeads"
                    class="pages-table"
                    :cols="tableHeadersLeads"
                    :list="metrics.bestLeads"
                    :has-pagination="false"
                    :has-header="true"
                  >
                    <template #title="{ row: page }">
                      <div class="column-title">
                        <IconLanguage
                          size="xs"
                          :language="page.language"
                        />
                        <span>{{ page.title }} </span>
                      </div>
                    </template>
                    <template #leads="{ row: page }">
                      <div class="column-leads">
                        {{ page.totalOfLeads }}
                      </div>
                    </template>
                    <template #actions="{ row: page }">
                      <div class="column-actions">
                        <button
                          class="column-actions__cta"
                          title="Abrir edição da página"
                          @click="goToEditor(page)"
                        >
                          Editar
                        </button>
                        <div>
                          <a
                            v-if="page.isPublished"
                            class="column-actions__cta"
                            :href="getPageAbsoluteUrl(page)"
                            target="_blank"
                            title="Abrir página em uma nova aba"
                          >
                            Ver página
                          </a>
                          <div
                            v-else
                            class="column-actions__cta column-actions__cta--disabled"
                            title="Página não publicada"
                          >
                            Ver página
                          </div>
                        </div>
                      </div>
                    </template>
                  </CustomTable>
                </div>
              </div>
            </div>
            <div class="card">
              <div class="left-side">
                <header class="card__title">Preenchimento de SEO</header>
                <Loader
                  v-if="loading"
                  color="#3e9aa6"
                ></Loader>
                <div
                  v-else
                  class="card__content"
                >
                  <RadialProgressBar
                    :progress="metrics.seo.total || 0"
                    :size="140"
                    :font-size="32"
                  />
                  <div class="card__label">
                    <div v-html="getSeoMessage(metrics.seo)"></div>
                    <router-link
                      v-if="metrics.seo.total > 0 && metrics.seo.total < 1"
                      id="back-to-pagelist"
                      :to="{ name: 'pagelist' }"
                    >
                      <br />Ir para lista de páginas.
                    </router-link>
                  </div>
                </div>
              </div>
              <div
                v-show="loading || metrics.worstSeo.length > 1"
                class="right-side"
              >
                <header class="card__title">
                  Top 5 páginas com menor preenchimento de SEO
                </header>
                <div class="card__content">
                  <Loader
                    v-if="loading"
                    color="#3e9aa6"
                  ></Loader>
                  <CustomTable
                    v-else
                    id="page-list"
                    ref="customTableSeo"
                    class="pages-table"
                    :cols="tableHeadersSeo"
                    :list="metrics.worstSeo"
                    :has-pagination="false"
                    :has-header="true"
                  >
                    <template #title="{ row: page }">
                      <div class="column-title">
                        <IconLanguage
                          size="xs"
                          :language="page.language"
                        />
                        <span>{{ page.title }} </span>
                      </div>
                    </template>
                    <template #seo="{ row: page }">
                      <RadialProgressBar
                        class="column-seo"
                        :progress="page.seo"
                        :title="seoTitleText(page.seo)"
                      />
                    </template>
                    <template #actions="{ row: page }">
                      <div class="column-actions">
                        <button
                          class="column-actions__cta"
                          title="Abrir edição da página"
                          @click="goToEditor(page)"
                        >
                          Editar
                        </button>
                      </div>
                    </template>
                  </CustomTable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import Header from '@/components/Header.vue';
import Footer from '@/components/Footer.vue';
import IconLanguage from '@/components/icons/IconLanguage.vue';
import RadialProgressBar from '@/components/RadialProgressBar.vue';
import IconSettings from '@/components/icons/IconSettings.vue';
import LineChart from '@/components/YearsLineChart.vue';
import Loader from '@/components/Loader.vue';
import isObject from '@/helpers/isObject';
import CustomTable from '../components/CustomTable.vue';

export default {
  name: 'MetricsDashboard',
  components: {
    Header,
    Footer,
    IconLanguage,
    RadialProgressBar,
    IconSettings,
    LineChart,
    CustomTable,
    Loader,
  },
  data() {
    return {
      tableHeadersLeads: [
        {
          name: 'title',
          label: 'Nome',
          isSortable: false,
        },
        {
          name: 'leads',
          label: 'Leads',
          maxWidth: '70px',
          isSortable: false,
        },
        {
          name: 'actions',
          label: 'Ações',
          maxWidth: '180px',
          isSortable: false,
        },
      ],
      tableHeadersSeo: [
        {
          name: 'title',
          label: 'Nome',
          isSortable: false,
        },
        {
          name: 'seo',
          label: 'SEO',
          maxWidth: '70px',
          isSortable: false,
        },
        {
          name: 'actions',
          label: 'Ações',
          maxWidth: '180px',
          isSortable: false,
        },
      ],
    };
  },
  computed: {
    ...mapState(['metrics', 'loading']),
    ...mapGetters(['siteHostname']),
  },
  methods: {
    getSeoMessage(seo) {
      if (seo.total === 0) {
        return 'Nenhuma página possui preenchimento completo dos campos de SEO. A falta de preenchimento afeta negativamente os resultados de busca orgânica.<br>Para começar a adicionar, basta selecionar uma das páginas e ir em “SEO” no canto esquerdo da edição.';
      }
      if (seo.total === 1) {
        return 'Excelente! Todas as páginas estão com os campos de SEO preenchidos. Se deseja aperfeiçoar ainda mais o SEO de seu site, <a href="https://pt.semrush.com/blog/como-melhorar-seo/" target="_blank">confira as dicas nesse link.</a>';
      }
      return `${seo.incompleted} páginas não estão com os campos de SEO preenchidos. Preencha os campos para obter melhores resultados nas suas buscas orgânicas.`;
    },
    seoTitleText(seo) {
      return seo === 1
        ? 'Sua página está com SEO completo!'
        : 'Sua página precisa melhorar. \nPreencha os campos de SEO.';
    },
    goToEditor(page) {
      this.$router.push({ name: 'editor', params: page });
    },
    getPageBaseUrl(page, protocol = true) {
      let pageBaseUrl = `${this.siteHostname}/`;

      if (protocol === true) {
        pageBaseUrl = `https://${pageBaseUrl}`;
      }

      if (isObject(page) && page.language !== 'pt') {
        pageBaseUrl += `${page.language}/`;
      }

      return pageBaseUrl;
    },
    getPageAbsoluteUrl(page) {
      let absolutePageUrl = this.getPageBaseUrl(page);

      absolutePageUrl += page.urlPath === '_' ? '' : page.urlPath;

      return absolutePageUrl;
    },
  },
};
</script>

<style scoped lang="scss">
.metrics {
  display: flex;
  flex-direction: column;
  height: 100vh;
  margin: 0;
  padding-top: 45px;

  &.loading {
    opacity: 0.75;
    cursor: progress;

    > * {
      pointer-events: none;
    }
  }

  &__content {
    height: 100%;
    padding: 64px 32px;
    overflow-y: auto;
  }

  &__container {
    width: 1232px;
    margin: auto;
  }

  &__dashboard {
    display: flex;
    flex-direction: column;
    gap: 32px;

    .card {
      border-radius: 24px;
      padding: 24px 48px;
      width: 100%;
      min-height: 350px;
      display: flex;
      flex-direction: row;

      + .card {
        margin-top: 32px;
      }

      .left-side {
        width: 40%;
        display: flex;
        flex-direction: column;
        margin: 0 auto;
      }

      .right-side {
        padding-left: 32px;
        width: 60%;
        display: flex;
        flex-direction: column;
      }

      .pages-table {
        width: 100%;
        padding-bottom: 0;
        min-height: unset;

        ::v-deep {
          th[data-identifier='seo'],
          td[data-identifier='seo'],
          th[data-identifier='leads'],
          td[data-identifier='leads'],
          th[data-identifier='actions'],
          td[data-identifier='actions'] {
            justify-content: center;
          }
          tr {
            height: 48px;
          }
        }

        .column-title {
          display: flex;
          align-items: center;
          width: 100%;

          .icon {
            flex-shrink: 0;
            margin-right: 12px;
          }
        }

        .column-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;

          > * + * {
            margin-left: 8px;
          }

          .icon {
            color: $crm-blue-primary;
            padding: 4px;
          }

          button[disabled] {
            opacity: 0.5;
          }

          &__cta {
            color: $crm-blue-primary;
            font-weight: $lb-medium;
            padding: 6px;

            &:hover {
              text-decoration: underline;
            }

            &--disabled {
              text-decoration: none;
              color: #7f7f7f;
              &:hover {
                text-decoration: none;
              }
            }
          }
        }

        .loader {
          width: 100%;
        }
      }

      &__title {
        color: $gray-90;
        font-size: 20px;
        font-weight: $lb-normal;
        margin: 0;
      }

      &__content {
        display: flex;
        justify-content: space-around;
        flex-direction: column;
        margin: auto 0;
      }

      &__statistics {
        display: flex;
        flex-direction: column;
      }

      &__amount {
        font-size: 42px;
        font-weight: 400;
        color: #474747;
        line-height: 1.2;
      }

      &__label {
        font-size: 14px;
        font-weight: 400;
        color: #7f7f7f;
        line-height: 1.2;
      }

      .radial-progress-bar {
        margin: 24px auto;
      }
    }
  }
}
</style>
